/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 *
 * API Credits Dashboard
 * Monitor API usage and credits across providers
 */

import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/AdminLayout';
import adminApi from '../../services/adminApi';

const Credits = () => {
  const [credits, setCredits] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadCredits();
  }, []);

  const loadCredits = async () => {
    try {
      setLoading(true);
      setError('');

      const [creditsRes, statsRes] = await Promise.all([
        adminApi.get('/credits/remaining').catch(() => ({ credits: [] })),
        adminApi.get('/credits/stats?days=30').catch(() => ({ stats: null }))
      ]);

      setCredits(creditsRes.credits || []);
      setStats(statsRes.stats || null);
    } catch (err) {
      setError(err.message || 'Failed to load credit data');
    } finally {
      setLoading(false);
    }
  };

  const getProgressColor = (percent) => {
    if (percent >= 90) return '#ef4444';
    if (percent >= 70) return '#f59e0b';
    return '#10b981';
  };

  if (loading) {
    return (
      <AdminLayout showBackButton={true} backPath="/admin/dashboard">
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <div className="spinner-large"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout showBackButton={true} backPath="/admin/dashboard">
      <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '24px' }}>
          <h1 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '8px' }}>
            💳 API Credits Dashboard
          </h1>
          <p style={{ color: '#6b7280' }}>
            Monitor API usage and remaining credits across providers
          </p>
        </div>

        {/* Error */}
        {error && (
          <div style={{
            padding: '12px 16px',
            backgroundColor: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '8px',
            color: '#dc2626',
            marginBottom: '24px'
          }}>
            {error}
          </div>
        )}

        {/* Credits Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '20px',
          marginBottom: '32px'
        }}>
          {credits.length === 0 ? (
            <div style={{
              gridColumn: '1 / -1',
              textAlign: 'center',
              padding: '48px',
              backgroundColor: '#f9fafb',
              borderRadius: '12px'
            }}>
              <p style={{ color: '#6b7280' }}>No credit data available</p>
              <p style={{ color: '#9ca3af', fontSize: '14px', marginTop: '8px' }}>
                Credit tracking will appear once API calls are logged
              </p>
            </div>
          ) : (
            credits.map((credit) => (
              <div key={credit.provider} style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '20px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <h3 style={{ fontWeight: '600', fontSize: '16px' }}>{credit.name}</h3>
                  <span style={{
                    padding: '4px 12px',
                    borderRadius: '9999px',
                    fontSize: '12px',
                    fontWeight: '500',
                    backgroundColor: credit.percentUsed >= 90 ? '#fef2f2' : credit.percentUsed >= 70 ? '#fefce8' : '#f0fdf4',
                    color: getProgressColor(credit.percentUsed)
                  }}>
                    {credit.percentUsed}% used
                  </span>
                </div>

                {/* Progress Bar */}
                <div style={{
                  width: '100%',
                  height: '8px',
                  backgroundColor: '#e5e7eb',
                  borderRadius: '4px',
                  overflow: 'hidden',
                  marginBottom: '12px'
                }}>
                  <div style={{
                    width: `${Math.min(credit.percentUsed, 100)}%`,
                    height: '100%',
                    backgroundColor: getProgressColor(credit.percentUsed),
                    transition: 'width 0.3s ease'
                  }} />
                </div>

                {/* Stats */}
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
                  <div>
                    <span style={{ color: '#6b7280' }}>Used: </span>
                    <span style={{ fontWeight: '500' }}>{credit.used.toLocaleString()}</span>
                  </div>
                  <div>
                    <span style={{ color: '#6b7280' }}>Remaining: </span>
                    <span style={{ fontWeight: '500', color: getProgressColor(credit.percentUsed) }}>
                      {credit.remaining.toLocaleString()}
                    </span>
                  </div>
                </div>

                <div style={{ marginTop: '8px', fontSize: '12px', color: '#9ca3af' }}>
                  Monthly limit: {credit.limit.toLocaleString()} tokens
                </div>
              </div>
            ))
          )}
        </div>

        {/* Usage Stats */}
        {stats && (
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <h2 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px' }}>
              📊 Usage Statistics (Last 30 Days)
            </h2>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
              gap: '16px'
            }}>
              <div style={{ textAlign: 'center', padding: '16px', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#3b82f6' }}>
                  {stats.totalCalls?.toLocaleString() || 0}
                </div>
                <div style={{ fontSize: '14px', color: '#6b7280' }}>Total API Calls</div>
              </div>

              {stats.byProvider?.map((provider) => (
                <div key={provider.provider} style={{ textAlign: 'center', padding: '16px', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#10b981' }}>
                    {parseInt(provider.total_calls || 0).toLocaleString()}
                  </div>
                  <div style={{ fontSize: '14px', color: '#6b7280' }}>{provider.provider}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Refresh Button */}
        <div style={{ marginTop: '24px', textAlign: 'center' }}>
          <button
            onClick={loadCredits}
            style={{
              padding: '12px 24px',
              backgroundColor: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '500'
            }}
          >
            🔄 Refresh Data
          </button>
        </div>
      </div>
    </AdminLayout>
  );
};

export default Credits;
